// src/utils/useSmartTableFilter.ts
import { useMemo, useState, useCallback, useEffect } from 'react';

type Key = string | number;

export type SmartFilterConfig<T> = {
  /** поле со статусом (если есть), например 'status' */
  statusField?: keyof T;
  /** какие поля участвуют в глобальном поиске; можно и вложенные "a.b.c" */
  textFields?: Array<keyof T | string>;
  /** режим глобального поиска по словам: 'any' — хоть одно, 'all' — все слова */
  matchMode?: 'any' | 'all';
  /** нормализация значений перед поиском */
  normalize?: (v: unknown) => string;
  /** задержка перед фильтрацией (мс). 0 или undefined — без дебаунса */
  debounceMs?: number;
};

// вложенный доступ по пути "a.b.c"
function getByPath(obj: any, path: string) {
  return String(path)
    .split('.')
    .reduce((acc: any, key) => (acc != null ? acc[key] : undefined), obj);
}

function defaultNormalize(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(defaultNormalize).join(' ');
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/** Универсальный хук фильтрации строк таблицы */
export function useSmartTableFilter<T>(rows: T[], cfg: SmartFilterConfig<T>) {
  const [activeStatuses, setActiveStatuses] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [colEq, setColEq] = useState<Record<string, Key | null>>({});

  // опциональный дебаунс для текста
  const [debouncedText, setDebouncedText] = useState(text);
  useEffect(() => {
    if (!cfg?.debounceMs) {
      setDebouncedText(text);
      return;
    }
    const id = setTimeout(() => setDebouncedText(text), cfg.debounceMs);
    return () => clearTimeout(id);
  }, [text, cfg?.debounceMs]);

  // чип статуса: включить/выключить
  const toggleStatus = useCallback((s: string) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }, []);

  // one-click фильтр по колонке
  const setColumnEq = useCallback((field: keyof T, value: Key | null) => {
    setColEq((prev) => ({ ...prev, [String(field)]: value }));
  }, []);
  const clearColumn = useCallback((field: keyof T) => {
    setColEq((prev) => ({ ...prev, [String(field)]: null }));
  }, []);
  const clearAll = useCallback(() => {
    setActiveStatuses(new Set());
    setText('');
    setColEq({});
  }, []);

  const filtered = useMemo(() => {
    const normalize = cfg?.normalize ?? defaultNormalize;
    const qRaw = cfg?.debounceMs ? debouncedText : text;
    const q = qRaw.trim().toLowerCase();
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];

    return rows.filter((r) => {
      // 1) фильтр по статусу
      if (cfg.statusField && activeStatuses.size) {
        const sv = String((r as any)[cfg.statusField] ?? '');
        if (!activeStatuses.has(sv)) return false;
      }

      // 2) equals-фильтры по колонкам
      for (const [field, v] of Object.entries(colEq)) {
        if (v == null) continue;
        const rv = (r as any)[field];
        if (String(rv) !== String(v)) return false;
      }

      // 3) глобальный поиск
      if (tokens.length && cfg.textFields?.length) {
        const hay = cfg.textFields
          .map((f) => {
            const val =
              typeof f === 'string' ? getByPath(r as any, f) : (r as any)[f as keyof T];
            return normalize(val);
          })
          .join(' ')
          .toLowerCase();

        if (cfg.matchMode === 'all') {
          for (const t of tokens) if (!hay.includes(t)) return false;
        } else {
          if (!tokens.some((t) => hay.includes(t))) return false;
        }
      }

      return true;
    });
  }, [
    rows,
    cfg.statusField,
    cfg.textFields,
    cfg.matchMode,
    cfg.normalize,
    cfg.debounceMs,
    activeStatuses,
    colEq,
    text,
    debouncedText,
  ]);

  return {
    filtered,
    searchText: text,
    setSearchText: setText,
    toggleStatus,
    isStatusActive: (s: string) => activeStatuses.has(s),
    setColumnEq,
    clearColumn,
    clearAll,
    state: { activeStatuses, colEq },
  };
}
